import React, { useState } from 'react';
import {
  TextInput,
  SelectInput,
  DateInput,
  TextField,
  EmailField,
  BooleanField,
  Button,
  Confirm,
  useRedirect,
  useDataProvider,
  downloadCSV
} from 'react-admin';

import * as jsonExport from "jsonexport/dist"
import SwitchIcon from '@mui/icons-material/Accessibility';
import CompanyIcon from '@mui/icons-material/CorporateFare';
import RemoveIcon from '@mui/icons-material/RemoveCircle';

import ListFilter from '../../components/poc/ListWithLayout/components/ListFilter';
import ListWithLayout from '../../components/poc/ListWithLayout'
import { StatusField } from '../../components/poc/StatusField';

import Actions from '../../components/poc/Grid/Row/Actions';
import { AVAILABLE_CLIENT_STATUSES, AVAILABLE_CLIENT_TYPES } from './consts';
import { deletePermission, switchPermission } from './permissions';
import config from '../../app/etc/config';
import session from '../../app/session';
import { useNotify, useTranslate } from 'ra-core';
import { STATUS_ACTIVE } from "../../app/etc/consts";

const ConfirmSwitchButton = (props) => {
  const { record } = props;

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const redirect = useRedirect();
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const handleClick = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);
  const handleConfirm = () => {
    setOpen(false);
    doSwitch(record);
  };

  const doSwitch = (record) => {
    setLoading(true);

    dataProvider
      .create('switch', { data: { client_id: record.id } })
      .then(response => {
        setLoading(false);

        session.switch(response.data);

        notify(translate('app.user.labels.switch_successful', { client_name: session.getClientName() }));
        redirect('/');
      })
      .catch(error => {
        setLoading(false);

        const msg = !!error.message ? error.message : 'An Error Happened';
        notify(`${msg}`, 'warning');
      });
  };

  return (
    <>
      <Button
        label="Switch"
        onClick={() => handleClick(record)}
        fullWidth={true}
      ><SwitchIcon /></Button>
      <Confirm
        isOpen={open}
        loading={loading}
        title="Switch On Behalf Of"
        content={`Are you sure you want to Switch On Behalf Of the "` + record.name + `" Client`}
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};

const ShopifyCompanyLocationButton = (props) => {
  const { record } = props;
  const [loading, setLoading] = useState(false);

  const notify = useNotify();
  const dataProvider = useDataProvider();

  const handleDownload = async (event) => {
    event.stopPropagation();
    event.preventDefault();

    setLoading(true);

    try {
      const rows = [];
      const { data } = await dataProvider.getCompanyData(record.id);

      for (const company of data.companyData) {
        const companyId = company.id.split('/').pop();
        for (const loc of company.locations) {
          const locId = loc.id.split('/').pop();
          if (loc.roleAssignments.length === 0) {
            rows.push({
              companyId,
              companyName: company.name,
              locationId: locId,
              locationName: loc.name,
              roleAssignmentId: '',
              roleId: '',
              roleName: '',
              contactId: '',
            });
          }
          for (const ra of loc.roleAssignments) {
            rows.push({
              companyId,
              companyName: company.name,
              locationId: locId,
              locationName: loc.name,
              roleAssignmentId: ra.id.split('/').pop(),
              roleId: ra.role.id.split('/').pop(),
              roleName: ra.role.name,
              contactId: ra.companyContactId.split('/').pop(),
            });
          }
        }
      }

      if (rows.length === 0) {
        notify('No data available to export for this company', { type: 'warning' });
        return; // Exit the try block early
      }

      jsonExport(rows, (err, csv) => {
        downloadCSV(csv, 'companies');
      });

      notify('ra.notification.csv_downloaded', {
        type: 'info',
        messageArgs: { _: 'CSV Downloaded Successfully' }
      });

    } catch (error) {
      const msg = error.message ? error.message : 'An Error Happened during CSV download';
      notify(`${msg}`, { type: 'warning' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      label={loading ? "Downloading..." : "Get Company Data"}
      onClick={handleDownload}
      disabled={loading}
      fullWidth={true}
    >
      <CompanyIcon />
    </Button>
  );
};

const ShopifyRemoveInvoiceButton = (props) => {
  const { record } = props;
  const [loading, setLoading] = useState(false);

  const notify = useNotify();
  const dataProvider = useDataProvider();

  const removeInvoice = async (event) => {
    event.stopPropagation();
    event.preventDefault();

    setLoading(true);

    try {
      const rows = [];
      const { response } = await dataProvider.removeInvoiceData(record.id);
    } catch (error) {
      const msg = error.message ? error.message : 'An Error Happened during CSV download';
      notify(`${msg}`, { type: 'warning' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      label={loading ? "Working..." : "Remove Invoice"}
      onClick={removeInvoice}
      disabled={loading}
      fullWidth={true}
    >
      <RemoveIcon />
    </Button>
  );
};

const ClientFilter = (props) => (
  <ListFilter {...props}>
    <TextInput source="id:in" label="ID" />
    <SelectInput
      choices={AVAILABLE_CLIENT_STATUSES}
      source="status"
    />
    <SelectInput
      choices={AVAILABLE_CLIENT_TYPES}
      source="type"
    />
    <TextInput source="code" />
    <TextInput source="name:like" label="Name" />
    <TextInput source="email" />
    <DateInput source="created_at" />
    <DateInput source="updated_at" />
  </ListFilter>
);

const CustomOptions = (record) => {
  const componentsArray = [];
  if (!record) return componentsArray;

  if (switchPermission(record) && record?.status === STATUS_ACTIVE) {
    componentsArray.push(
      <ConfirmSwitchButton record={record} key="switch" />
    );
  }

  if (record?.platform?.source === 'shopifycloud') {
    componentsArray.push(
      <ShopifyCompanyLocationButton record={record} key="shopify-company-location" />
    );
        componentsArray.push(
      <ShopifyRemoveInvoiceButton record={record} key="shopify-remove-invoice" />
    );
  }

  return componentsArray;
};

export const ClientGrid = props => {
  return (
    <ListWithLayout
      {...props}
      createButton={true}
      filters={<ClientFilter />}
      resource="client"
      perPage={config.pagination.grid}
      sort={{ field: 'created_at', order: 'DESC' }}
    >
      <TextField source="id" sortable={false} />
      <TextField source="type" />
      <StatusField source="status" />
      <TextField source="code" />
      <TextField source="name" />
      <EmailField source="email" />
      <BooleanField source="is_system" sortable={false} />

      <TextField source="platform.source" label="Platform" sortable={false} emptyText={config.platform.default} />

      <Actions edit={true} delete={deletePermission} customOptionsAfter={CustomOptions} />
    </ListWithLayout>
  )
}
